/**
 * @file src/room/roomPanelBridge.js
 * @description The narrow seam between the two owners of a synced per-deck VIEW state: each voice's
 * React UI (owns the state — the interaction panel, the Info-panel open/close, the waveform panel's
 * selected loop subdivision, the gain rail's IN|OUT monitoring selection) and the room client (owns
 * the wire — it publishes and applies the mirror). No-window-globals module singleton, the same pattern the room store / deck bridge use.
 *
 * "See the same" includes per-deck UI state: when a holder switches a deck to (say) the
 * waveform panel, everyone — players AND spectators — sees that deck in that panel. So the PUBLISH
 * of a local change is owner-gated (done in the room client), but the APPLY is not — every client
 * mirrors it. The same holds for the Info panel's open/close.
 *
 * Directions across this seam:
 *  - `notifyLocalPanel` / `notifyLocalInfo` (voice UI → wire): a local panel/info change — publish it.
 *  - `applyRemotePanel` / `applyRemoteInfo` (wire → voice UI): a remote change — mirror it locally.
 *
 * Echo guard: `applyRemote*` records the applied value BEFORE driving the UI, so the UI's own
 * change notification (which fires synchronously from the apply) dedups against it in `notifyLocal*`
 * instead of bouncing back onto the wire.
 *
 * The four mirrors read as one pattern written four times — a full consolidation was built and
 * MEASURED (2026-08) and does not pay: the shared skeleton is ~50 code lines while the machinery
 * to declare the real per-mirror differences costs ~80. Each body mostly IS its difference —
 * pending maps vs the last-value map answering late mounts, per-DIRECTION validation (the panel
 * notify publishes unvalidated; only its apply validates), asymmetric snapshot fallbacks, and the
 * gain switch's refusable boolean publish. Fold them only when a FIFTH mirror appears; that flips
 * the economics.
 */

// runtime voiceId → { activate(panelId), setInfoMode(mode|null) } — registered by each voice's UI.
const surfaces = new Map();
// runtime voiceId → setLoopSize(beats|null). Its OWN map, not a field on `surfaces`: the waveform
// panel mounts and unmounts independently of the tile that owns the panel surface, so one
// registration must never clobber the other.
const loopSizeSurfaces = new Map();
// runtime voiceId → setGainChannel('input'|'output'). Its own map for the same reason: the gain rail
// mounts and unmounts independently of the tile that owns the panel surface.
const gainChannelSurfaces = new Map();
// Room-client publishers (set only in room mode; null otherwise → local changes publish nothing).
let publishPanel = null; // (voiceId, panelId) => void
let publishInfo = null; // (voiceId, mode|null) => void
let publishLoopSize = null; // (voiceId, beats|null) => void
let publishGainChannel = null; // (voiceId, 'input'|'output') => boolean
// Last value applied/published per voice — dedups an apply's synchronous echo + a no-op repeat.
const lastPanel = new Map();
const lastInfo = new Map();
// The selected loop subdivision per voice (beats, or null for "no preset describes this loop").
// Deliberately held HERE rather than only in the panel's React state: the value must outlive the
// waveform panel's mount, so a remote pick that arrives while the panel is closed is still the
// selection the panel shows when it opens, and a snapshot can answer for a deck nobody is looking at.
const lastLoopSize = new Map();
// Which gain stage each voice's rail is pointed at. Held here for the same reason as the loop size:
// the value must outlive the rail's mount so a remote flip that arrives early is still what the rail
// shows when it appears, and a snapshot can answer for a deck nobody is looking at.
const lastGainChannel = new Map();
// Remote values that arrived BEFORE the voice's UI registered its surface (a late-join snapshot
// applies while the tile is still mounting) — drained onto the surface when it registers.
const pendingPanel = new Map();
const pendingInfo = new Map();
// True while a REMOTE panel mirror is being driven into the UI. The mirror shares WHERE a deck
// is, never the acquisition process behind getting there: panels whose entry starts a device
// acquisition (sensor permission prompt, phone-pairing dialog, MIDI access request) consult this
// to show the panel without prompting — on this device those flows open only from a local gesture.
let applyingRemotePanel = false;

/** Is the current panel activation a remote mirror apply (vs a local gesture)? */
export function isApplyingRemotePanelMirror() {
  return applyingRemotePanel;
}

/**
 * Voice UI → wire: register how a remote change reaches this voice, seeding its current values.
 * @param {string} voiceId
 * @param {{ activate?: ((panelId: string) => void)|null,
 *           setInfoMode?: ((mode: string|null) => void)|null,
 *           panelId?: string|null,
 *           infoMode?: string|null }} [handlers]
 */
export function registerVoicePanelSurface(voiceId, { activate = null, setInfoMode = null, panelId = null, infoMode = null } = {}) {
  if (!voiceId) return;
  surfaces.set(voiceId, {
    activate: typeof activate === 'function' ? activate : null,
    setInfoMode: typeof setInfoMode === 'function' ? setInfoMode : null,
  });
  lastPanel.set(voiceId, panelId);
  lastInfo.set(voiceId, infoMode);
  // A remote mirror that arrived before this surface existed applies NOW (late-join snapshot).
  if (pendingPanel.has(voiceId)) {
    const pending = pendingPanel.get(voiceId);
    pendingPanel.delete(voiceId);
    applyRemotePanel(voiceId, pending);
  }
  if (pendingInfo.has(voiceId)) {
    const pending = pendingInfo.get(voiceId);
    pendingInfo.delete(voiceId);
    applyRemoteInfo(voiceId, pending);
  }
}

/** Voice UI → wire: forget a voice (its UI unmounted). */
export function unregisterVoicePanelSurface(voiceId) {
  surfaces.delete(voiceId);
  lastPanel.delete(voiceId);
  lastInfo.delete(voiceId);
  pendingPanel.delete(voiceId);
  pendingInfo.delete(voiceId);
  // NOT lastLoopSize — that outlives the panel on purpose (see the map's comment); the voice's own
  // teardown drops it via `forgetVoiceViewState`.
}

/**
 * Waveform panel → wire: register how a remote loop-subdivision pick reaches this voice's panel.
 * Returns nothing; call `unregisterVoiceLoopSizeSurface` on unmount.
 * @param {string} voiceId
 * @param {(beats: number|null) => void} setLoopSize
 */
export function registerVoiceLoopSizeSurface(voiceId, setLoopSize) {
  if (!voiceId || typeof setLoopSize !== 'function') return;
  loopSizeSurfaces.set(voiceId, setLoopSize);
}

/** Waveform panel → wire: the panel unmounted (the remembered selection stays — see `lastLoopSize`). */
export function unregisterVoiceLoopSizeSurface(voiceId) {
  loopSizeSurfaces.delete(voiceId);
}

/**
 * Drop a voice's remembered view state — the VALUES only, never the surface registration.
 *
 * The room client calls this as it detaches a voice, which includes its own `dispose()`: leaving a
 * room, a failed handshake, a dropped connection. The tiles stay MOUNTED through all of those, and a
 * surface is registered by a React effect keyed on the voice id — so deleting the registration here
 * would never be undone, and joining a second room in the same page session would leave the waveform
 * panel and the gain rail silently deaf to every remote mirror. Unregistering belongs to the React
 * unmount, exactly as the panel surface already does it.
 */
export function forgetVoiceViewState(voiceId) {
  lastLoopSize.delete(voiceId);
  lastGainChannel.delete(voiceId);
}

/**
 * Gain rail → wire: register how a remote IN|OUT flip reaches this voice's rail.
 * @param {string} voiceId
 * @param {(channel: 'input'|'output') => void} setGainChannel
 */
export function registerVoiceGainChannelSurface(voiceId, setGainChannel) {
  if (!voiceId || typeof setGainChannel !== 'function') return;
  gainChannelSurfaces.set(voiceId, setGainChannel);
}

/** Gain rail → wire: the rail unmounted (the remembered selection stays — see `lastGainChannel`). */
export function unregisterVoiceGainChannelSurface(voiceId) {
  gainChannelSurfaces.delete(voiceId);
}

/** The stage a freshly-mounting gain rail should open on, or null for its own default. */
export function voiceGainChannel(voiceId) {
  return lastGainChannel.get(voiceId) ?? null;
}

/**
 * @returns {boolean} whether the rail may SHOW the flip. True outside a room (the rail is its own
 * owner, and nothing is remembered — no peer to converge with) and when the publisher took it;
 * false when the room refused: the switch is one shared view, and a press that cannot re-point it
 * must not flip a private copy that the holder's next snapshot would only snap back.
 */
export function notifyLocalGainChannel(voiceId, channel) {
  if (!voiceId) return true; // single-orbiter: no per-deck identity to mirror
  if (channel !== 'input' && channel !== 'output') return false;
  if (!publishGainChannel) return true;
  if (lastGainChannel.get(voiceId) === channel) return true; // already the shared view
  // Publish BEFORE recording: a refused flip must leave the remembered shared view untouched, or
  // the next REAL mirror of that same stage would dedup against the poisoned entry and vanish.
  if (publishGainChannel(voiceId, channel) === false) return false;
  lastGainChannel.set(voiceId, channel);
  return true;
}

/** Wire → gain rail: mirror a remote IN|OUT flip (echo-guarded). Recorded even with no rail mounted. */
export function applyRemoteGainChannel(voiceId, channel) {
  if (!voiceId || (channel !== 'input' && channel !== 'output')) return;
  lastGainChannel.set(voiceId, channel); // record BEFORE the UI change so its notify dedups
  gainChannelSurfaces.get(voiceId)?.(channel);
}

/** The selection a freshly-mounting waveform panel should open on (null = no preset). */
export function voiceLoopSize(voiceId) {
  return lastLoopSize.get(voiceId) ?? null;
}

/** Waveform panel → wire: the local loop-subdivision selection changed — publish it. Outside a room
 *  (no publisher installed) this is a no-op and nothing is remembered: there is no peer to converge
 *  with, so a solo panel keeps its historical "reopening forgets the preset" behaviour and the map
 *  never accumulates entries for voices that come and go. */
export function notifyLocalLoopSize(voiceId, beats) {
  if (!voiceId || !publishLoopSize) return;
  const next = Number.isFinite(beats) ? beats : null;
  if (lastLoopSize.has(voiceId) && lastLoopSize.get(voiceId) === next) return;
  lastLoopSize.set(voiceId, next);
  publishLoopSize(voiceId, next);
}

/** Wire → waveform panel: mirror a remote loop-subdivision pick (echo-guarded). Recorded even with
 *  no panel mounted, so the panel opens on the room's selection rather than a stale local one. */
export function applyRemoteLoopSize(voiceId, beats) {
  if (!voiceId) return;
  const next = Number.isFinite(beats) ? beats : null;
  lastLoopSize.set(voiceId, next); // record BEFORE the UI change so its notify dedups, not echoes
  loopSizeSurfaces.get(voiceId)?.(next);
}

/** Voice UI → wire: the local interaction panel changed — publish it (no-op if unchanged / outside a room). */
export function notifyLocalPanel(voiceId, panelId) {
  if (!voiceId || lastPanel.get(voiceId) === panelId) return;
  lastPanel.set(voiceId, panelId);
  publishPanel?.(voiceId, panelId);
}

/** Voice UI → wire: the local Info panel opened/closed/switched — publish it. */
export function notifyLocalInfo(voiceId, mode) {
  if (!voiceId || lastInfo.get(voiceId) === mode) return;
  lastInfo.set(voiceId, mode);
  publishInfo?.(voiceId, mode);
}

/** Wire → voice UI: mirror a remote interaction-panel change (echo-guarded). Held until the
 *  voice's surface registers when it hasn't yet (snapshots can outrun the tile mount). */
export function applyRemotePanel(voiceId, panelId) {
  if (!voiceId || typeof panelId !== 'string' || panelId.length === 0) return;
  const surface = surfaces.get(voiceId);
  if (!surface?.activate) {
    pendingPanel.set(voiceId, panelId);
    return;
  }
  lastPanel.set(voiceId, panelId); // record BEFORE the UI change so its notify dedups, not echoes
  applyingRemotePanel = true;
  try {
    surface.activate(panelId);
  } finally {
    applyingRemotePanel = false;
  }
}

/** Wire → voice UI: mirror a remote Info-panel change (`null` = closed) (echo-guarded). Held
 *  until the voice's surface registers when it hasn't yet. */
export function applyRemoteInfo(voiceId, mode) {
  if (!voiceId) return;
  const next = mode == null ? null : mode;
  const surface = surfaces.get(voiceId);
  if (!surface?.setInfoMode) {
    pendingInfo.set(voiceId, next);
    return;
  }
  lastInfo.set(voiceId, next);
  surface.setInfoMode(next);
}

/** The voice's mirrored view state for a `player:state` snapshot ({ panelId, infoMode, loopSize }).
 *  Reads the same last-value maps the echo guard keeps, so it reflects local AND applied changes. */
export function voicePanelSnapshot(voiceId) {
  return {
    panelId: lastPanel.get(voiceId) ?? pendingPanel.get(voiceId) ?? null,
    infoMode: lastInfo.has(voiceId) ? (lastInfo.get(voiceId) ?? null) : (pendingInfo.get(voiceId) ?? null),
    loopSize: lastLoopSize.get(voiceId) ?? null,
    gainChannel: lastGainChannel.get(voiceId) ?? null,
  };
}

/** The room client registers how a local view-state change reaches the wire (owner-gated inside). */
export function setRoomPanelPublishers({ panel = null, info = null, loopSize = null, gainChannel = null } = {}) {
  publishPanel = typeof panel === 'function' ? panel : null;
  publishInfo = typeof info === 'function' ? info : null;
  publishLoopSize = typeof loopSize === 'function' ? loopSize : null;
  publishGainChannel = typeof gainChannel === 'function' ? gainChannel : null;
}

/** Drop every registration (teardown / tests). */
export function resetPanelBridge() {
  surfaces.clear();
  lastPanel.clear();
  lastInfo.clear();
  pendingPanel.clear();
  pendingInfo.clear();
  loopSizeSurfaces.clear();
  lastLoopSize.clear();
  gainChannelSurfaces.clear();
  lastGainChannel.clear();
  publishPanel = null;
  publishInfo = null;
  publishLoopSize = null;
  publishGainChannel = null;
}
